import { Track } from "../../../interfaces/dtos/track"
import { TrackFormWrapperProps } from "./TrackFormWrapper";
import { Form, FormAdditionalItem, FormButton, FormButtonContainer, FormItem, FormProps, isMobileView, OptionProps, SelectInput, TextInput, useAlerts, useForm } from "@sis/react-library";
import { categoryItems } from "../../shared/sharedHelper";
import { Provider } from "../../../interfaces/dtos/common";
import { useLocation, useNavigate } from "react-router-dom";
import { trackRoutes } from "../TrackNavigation";
import { GetCountryOptions } from "../../shared/ListOptions";

// allow no members for shorter name to be more readable below
// eslint-disable-next-line
interface T extends Track { }

interface TrackFormProps extends TrackFormWrapperProps {
    providers: Provider[];
}

export const TrackForm = ({
    track, providers, onSubmit, submitting
}: TrackFormProps & FormProps<T>) => {

    const formName = 'trackForm';
    const forNew = track.sportCode === undefined;
    const isMobile = isMobileView();    
    const navigate = useNavigate();
    const location = useLocation();
    const { pristine, resetForm } = useForm<T>(formName);
    const { confirm } = useAlerts();

    const returnToList = () => {
        navigate(`${trackRoutes.main}${location.search}`);
    }

    const providerOptions: OptionProps[] = providers.map(s => ({
        value: s.code,
        description: s.description
    }));

    const categoryOptions: OptionProps[] = categoryItems.map(ci => ({
        value: ci.key,
        description: ci.value
    }));

    const countryOptions = GetCountryOptions(undefined, track?.countryCode);

    const handleCancel = () => {
        if (pristine) {
            returnToList();
        } else {
            confirm('Cancel', 'Are you sure you want to cancel and lose your changes?',
                () => { resetForm() },
                "Yes", "No"
            );
        }
    }

    const additionalItems: FormAdditionalItem<T>[] = [
        { name: "key" }, { name: "trackCodeKey" },
        { name: "isEPDisplay" }, { name: "isEPDistribute" }, { name: "isBPDisplay" }, { name: "isBPDistribute" },
        { name: "winMarketAlert" }, { name: "drvMarketAlert" }, { name: "epInactiveAlert" }, { name: "bpInactiveAlert" },
        { name: "isTrackActive" }
    ]

    return (
        <Form<T>
            name={formName}
            onSubmit={onSubmit}
            submitting={submitting}
            beforeLeaveText='You have made changes to this track, are you sure you want to leave?'
            initialValues={track}
            additionalItems={additionalItems}
        >
            <FormItem<T>
                formField="sportCode"
                label="Sport Code"
                noFeedback={!forNew}
                rules={[{ type: "required", message: "Please enter a sport code!" }]}
                element={<TextInput autoFocus={!isMobile && forNew} maxLength={2} disabled={!forNew} />}
            />
            <FormItem<T>
                formField="provider"
                label="Provider"
                rules={[{ type: "required", message: "Please select a provider!" }]}
                element={<SelectInput options={providerOptions} hideBlankSelection hideFilterSearch disabled={!forNew} />}
            />
            <FormItem<T>
                formField="categoryCode"
                label="Category"
                rules={[{ type: "required", message: "Please select a category!" }]}
                element={<SelectInput options={categoryOptions} hideBlankSelection hideFilterSearch disabled={!forNew} />}
            />
            <FormItem<T>
                formField="countryCode"
                label="Country"
                rules={[{ type: "required", message: "Please select a country!" }]}
                element={<SelectInput options={countryOptions} disabled={!forNew} />}
            />
            <FormItem<T>
                formField="shortDescription"
                label="Short Description"
                rules={[{ type: "required", message: "Please enter a short description!" }]}
                element={<TextInput autoFocus={!isMobile && !forNew} maxLength={50} />}
            />
            <FormItem<T>
                formField="longDescription"
                label="Long Description"
                rules={[{ type: "required", message: "Please enter a long description!" }]}
                element={<TextInput maxLength={50} />}
            />
            <FormButtonContainer>
                <FormButton
                    formButtonText={pristine ? "Close" : "Cancel"}
                    enabledOnPristine
                    onClick={handleCancel}
                />
                <FormButton
                    formButtonText={forNew ? 'Create' : 'Update'}
                    submitButton
                />
            </FormButtonContainer>
        </Form>
    )
}
